import "./BundlePrice.scss";

import * as _ from "lodash";

import * as PropTypes from "prop-types";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { getDisplayPriceData, primaryIsNet } from "../../../../../utils/PriceDisplayFramework";
import { getDerivedPricePromotionInfo, getDnrLabels, prohibitedDnrLabels } from "shared/src/utils/PriceDataHelper";
import { connect } from "shared/src/stores/storeConnect";
import NoPrice from "./NoPrice";
import MoneyAmount from "../../../lists/MoneyAmount";
import LinkContainer from "../../../../general/LinkContainer";
import * as UrlUtil from "../../../../../api/UrlUtil";
import {
    BulkDiscountSourceLevel,
    SellingPriceDnrInfo,
    PriceAndPromotionInfo
} from "shared/src/types/PriceAndPromotionInfo";
import { PriceHiddenReasons } from "shared/src/types/articlesearch/Article";
import { PriceDisplayData } from "../../../../../datatypes/Price";
import { WeightPerPieceUnit } from "shared/src/types/articlesearch/Article";
import SessionStore, { isTier12Customer } from "shared/src/stores/SessionStore";
import { PriceConfigStore } from "shared/src/stores/PriceConfigStore";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { isBulkPricesFeatureActive, isXForYFeatureActive } from "../../../../../searchdiscover/toggles";
import { getComponentByName } from "shared/src/components/LazyWrapper";
import { PriceConfig } from "shared/src/types/articlesearch/Price";
import { SellingPriceDnRInfo } from "shared/src/types/EvaluateArticleNew";
const BulkDiscountTiersDisplay = getComponentByName("sd_pdp_ui/BulkDiscountTiersDisplay");

type Props = {
    id: string;
    priceAndPromotionInfo: PriceAndPromotionInfo;
    loggedIn?: boolean;
    amountInBasket: number | null;
    isTier12Customer?: boolean;
    isWeightArticle: boolean;
    weightPerPiece: WeightPerPieceUnit | undefined | null;
    priceConfig: PriceConfig;
    secondaryPriceConfig?: string;
    isEmployee?: boolean;
};

type State = {
    xValueForPromotion: Number | null;
    yValueForPromotion: Number | null;
};

class BundlePrice extends React.Component<Props, State> {
    static contextTypes = {
        intl: PropTypes.object.isRequired,
        router: PropTypes.object.isRequired
    };

    constructor(props: Props) {
        super(props);
        this.state = { xValueForPromotion: null, yValueForPromotion: null };
    }

    componentWillReceiveProps(nextProps: Props) {
        const dnrInfo = nextProps.priceAndPromotionInfo.dnrInfo;
        for (const dnrId in dnrInfo) {
            const dnr = dnrInfo[dnrId];
            const messageKey = this._getMessageKeyForDnrBadge(dnr.labelId);
            const labelKeys = this._getLabelKeysForDnrBadge(dnr.labelId);
            const derivedDnrLevels: Array<BulkDiscountSourceLevel> = getDerivedPricePromotionInfo(
                nextProps.priceAndPromotionInfo
            );
            const labelValues = this._getValuesForDnrBadge(dnr, derivedDnrLevels);
            if (!messageKey || labelKeys.length !== labelValues.length) {
                continue;
            }
            const values = _.zipObject(labelKeys, labelValues);
            if (messageKey === "SD_PROMOTION_X_FOR_Y") {
                this.setState({ xValueForPromotion: Number(values.x), yValueForPromotion: Number(values.y) });
            }
        }
    }

    renderPriceWithMessage(price: PriceDisplayData, currency: string, promo: React.ReactNode, style: string) {
        const unitIndicatorKey =
            this.props.isWeightArticle && price.unitIndicatorWeight ? price.unitIndicatorWeight : null;
        return (
            <div>
                {promo}
                <p className={style}>
                    <MoneyAmount
                        amount={{ amount: price.amount || 0, currency }}
                        unitIndicatorKey={unitIndicatorKey}
                        labelKey={price.info}
                    />
                </p>
            </div>
        );
    }

    renderOnlyMessage(price: PriceDisplayData, style: string) {
        const message = this.context.intl.messages[price.info!];
        return !_.isEmpty(message) && <p className={style}>{message}</p>;
    }

    _getMessageKeyForDnrBadge(labelId: string | undefined): string | undefined | null {
        const messageKey = "EX_DNR_LABEL." + labelId;
        if (!_.isEmpty(this.context.intl.messages[messageKey])) {
            return messageKey;
        }
        if (labelId === "X_FOR_Y") {
            // grandfathered-in special case implementation
            return "SD_PROMOTION_X_FOR_Y";
        }
        if (isBulkPricesFeatureActive() && labelId === "MARKETPLACE_VOLUME_PRICE") {
            return "SD_PROMOTION_BULK_PRICE_TEMP";
        }
        return null;
    }

    _getRefinedMessageKeyForBulkDiscountDnrBadge(dnrLabels: Array<string> | null | undefined, values: any): string {
        if (
            // FIXME(DaBi): This custom check should be largely avoided in the future once we proceed with finalizing bulk prices.
            //  This is because checks for whether the shared table component will show up or not should be simplified
            //  to a simple check for whether the table component rendered to null or not instead of performing manual checks
            //  in advance here.
            dnrLabels &&
            _.intersection(dnrLabels, prohibitedDnrLabels).length === 0 &&
            values?.percentSaved &&
            values.percentSaved !== "0"
        ) {
            return "SD_PROMOTION_BULK_PRICE_WITH_PERCENTAGE";
        }
        return "SD_PROMOTION_BULK_PRICE_WITHOUT_PERCENTAGE";
    }

    // Price gives us a list of values that can be used in the badge, but no names for them, so we have to make some
    // fitting ones up.
    _getLabelKeysForDnrBadge(labelId: string | undefined): string[] {
        switch (labelId) {
            case "X_FOR_Y":
                return ["x", "y"];
            case "MARKETPLACE_VOLUME_PRICE":
                return ["percentSaved"];
            default:
                return [];
        }
    }

    _getPercentSavedForBulkPrices(derivedDnrLevels: Array<BulkDiscountSourceLevel>): string[] {
        const amountInBasket = Number(this.props.amountInBasket);
        if (!amountInBasket) {
            return ["0"];
        }
        for (let i = derivedDnrLevels.length - 1; i >= 0; i--) {
            const amountStart = Number(derivedDnrLevels[i].amountStart);
            if (amountStart && amountInBasket >= amountStart) {
                return [(Number(derivedDnrLevels[i].discountPercent) * 100).toString()];
            }
        }
        return ["0"];
    }

    _getValuesForDnrBadge(
        dnr: SellingPriceDnrInfo | SellingPriceDnRInfo,
        derivedDnrLevels: Array<BulkDiscountSourceLevel>
    ): string[] {
        switch (dnr.labelId) {
            case "MARKETPLACE_VOLUME_PRICE":
                return this._getPercentSavedForBulkPrices(derivedDnrLevels);
            default:
                return dnr.labelValues || [];
        }
    }

    getXForYClassName() {
        return (
            "x-for-y " +
            (this.state.xValueForPromotion &&
            this.state.yValueForPromotion &&
            this.props.amountInBasket !== null &&
            this.props.amountInBasket >= this.state.xValueForPromotion.valueOf()
                ? "active "
                : "inactive")
        );
    }

    renderXForYMessage() {
        if (
            !(this.props.amountInBasket !== null) ||
            !this.state ||
            !this.state.yValueForPromotion ||
            !this.state.xValueForPromotion
        ) {
            return null;
        }
        const currency = this.props.priceAndPromotionInfo.currency;
        const unitPrice = this.props.priceAndPromotionInfo.unitNet;
        if (isXForYFeatureActive() && currency && unitPrice) {
            const x = this.state.xValueForPromotion.valueOf();
            const y = this.state.yValueForPromotion.valueOf();
            const totalSavings = this.props.priceAndPromotionInfo.promotionSavings;
            const basketContainsEnoughItemsForPromotion = this.props.amountInBasket > y;
            const showTotalSavings = basketContainsEnoughItemsForPromotion && totalSavings && totalSavings > 0;
            if (showTotalSavings && totalSavings) {
                const totalSavingsFormatted = <MoneyAmount amount={{ amount: totalSavings, currency }} />;
                return (
                    <div className={"sd-x-for-y-savings-text"}>
                        <FormattedMessage id="SD_X_FOR_Y_SAVED" values={{ totalSavingsFormatted }} />
                    </div>
                );
            }
            const pricePerQuantity = <MoneyAmount amount={{ amount: unitPrice, currency }} />;
            return (
                <div className={"sd-x-for-y-savings-text"}>
                    <FormattedMessage id="SD_X_FOR_Y_ADD_TO_SAVE" values={{ pricePerQuantity, x }} />
                </div>
            );
        }
        return null;
    }

    renderBulkDiscountTable(
        derivedDnrLevels: Array<BulkDiscountSourceLevel>,
        dnrLabels: Array<string>,
        currency: string | null | undefined
    ) {
        // FIXME(DaBi): outsource the below in a separate wrapper-component to avoid code duplication (since similar code
        //  is used in AmountChooser as well)
        // FIXME(KiHu): This is only necessary because of naming incosistencies between explore-ui and pdp-ui.
        const weightPerPiece = this.props.weightPerPiece
            ? {
                  value: this.props.weightPerPiece.value,
                  uom: this.props.weightPerPiece.unit
              }
            : null;
        const bulkDiscountTable = BulkDiscountTiersDisplay({
            summaryDnrLevels: derivedDnrLevels,
            currency: currency,
            amountInCart: this.props.amountInBasket,
            isWeightedArticle: this.props.isWeightArticle,
            weightPerPiece: weightPerPiece,
            useNetPrice: primaryIsNet(this.props.priceConfig),
            dnrLabels: dnrLabels
        });
        return bulkDiscountTable;
    }

    renderOverlayTrigger(
        derivedDnrLevels: Array<BulkDiscountSourceLevel>,
        dnrLabels: Array<string>,
        currency: string | null | undefined,
        messageKey: string
    ) {
        const bulkDiscountsTable = this.renderBulkDiscountTable(derivedDnrLevels, dnrLabels, currency);
        if (bulkDiscountsTable) {
            return (
                <OverlayTrigger
                    trigger={["click", "hover", "focus"]}
                    placement="top"
                    overlay={<Popover id={messageKey + "_popover"}>{bulkDiscountsTable}</Popover>}>
                    <span className="mfcss_info-icon sd-promotion-label-info" />
                </OverlayTrigger>
            );
        }
        return null;
    }

    renderDnrBadges() {
        const priceInfo = this.props.priceAndPromotionInfo;
        const dnrInfo = priceInfo.dnrInfo;
        const result = [];
        for (const dnrId in dnrInfo) {
            const dnr = dnrInfo[dnrId];
            const derivedDnrLevels: Array<BulkDiscountSourceLevel> = getDerivedPricePromotionInfo(priceInfo);
            const messageKey = this._getMessageKeyForDnrBadge(dnr.labelId);
            const labelKeys = this._getLabelKeysForDnrBadge(dnr.labelId);
            const labelValues = this._getValuesForDnrBadge(dnr, derivedDnrLevels);
            if (!messageKey || labelKeys.length !== labelValues.length) {
                continue;
            }
            const values = _.zipObject(labelKeys, labelValues);
            const isDnrApplied = priceInfo.appliedDnrIds && priceInfo.appliedDnrIds.includes(dnrId);
            if (messageKey === "SD_PROMOTION_X_FOR_Y") {
                const className = "label-rectangular " + this.getXForYClassName();
                if (isXForYFeatureActive()) {
                    result.push(
                        <div className={className} key={dnrId}>
                            <span>
                                <FormattedMessage id={messageKey} values={values} defaultMessage={dnr.name} />
                            </span>
                        </div>
                    );
                }
            } else if (messageKey === "SD_PROMOTION_BULK_PRICE_TEMP") {
                const className =
                    "label-rectangular sd-label-rectangular-extension " +
                    (isDnrApplied ? "label-promotion" : "label-promotion-inactive");
                const dnrLabels: Array<string> = getDnrLabels(priceInfo);
                const refinedMessageKey: string = this._getRefinedMessageKeyForBulkDiscountDnrBadge(dnrLabels, values);
                result.push(
                    <div className={className} key={dnrId}>
                        <span>
                            <FormattedMessage
                                id={refinedMessageKey}
                                values={values}
                                defaultMessage={refinedMessageKey}
                            />
                            {this.renderOverlayTrigger(
                                derivedDnrLevels,
                                dnrLabels,
                                priceInfo.currency,
                                refinedMessageKey
                            )}
                        </span>
                    </div>
                );
            } else {
                const className =
                    "label-rectangular " + (isDnrApplied ? "label-promotion" : "label-promotion-inactive");
                result.push(
                    <div className={className} key={dnrId}>
                        <span>
                            <FormattedMessage id={messageKey} values={values} defaultMessage={dnr.name} />
                        </span>
                    </div>
                );
            }
        }
        return result;
    }

    isLoggedInTier34CustomerOrEmployee() {
        return this.props.loggedIn && !this.props.isTier12Customer;
    }

    render() {
        const priceInfo = this.props.priceAndPromotionInfo;
        const displayData = getDisplayPriceData(
            priceInfo,
            this.props.priceConfig,
            this.props.isWeightArticle,
            this.props.secondaryPriceConfig
        );
        // We can't just use FormattedMessage here because that doesn't like empty messages
        const dnrBadges = this.renderDnrBadges();
        const location = this.context.router.getCurrentLocation();
        const pathName = UrlUtil.getRelativePathForRedirectAfterLogin(location);
        const primary = displayData.primary;
        const secondary = displayData.secondary;
        const strikeThrough = primary && primary.strikeThrough;
        const currency = displayData.currency;
        const renderPrimary = primary && (!_.isNil(primary.amount) || primary.renderDescriptionOnly);
        const renderSecondary = secondary && (!_.isNil(secondary.amount) || secondary.renderDescriptionOnly);
        const priceHiddenAnonUser =
            this.props.priceAndPromotionInfo.priceHiddenReason ===
            PriceHiddenReasons.ANON_CUSTOMER_VIEWING_FOOD_ARTICLE;
        const nonFoodCustomer =
            this.props.priceAndPromotionInfo.priceHiddenReason ===
            PriceHiddenReasons.NONFOOD_CUSTOMER_VIEWING_FOOD_ARTICLE;
        const status20User =
            this.props.priceAndPromotionInfo.priceHiddenReason === PriceHiddenReasons.STATUS_20_NOSTOCK_ARTICLE;
        const isStatus20Employee = status20User && this.props.isEmployee;
        return (
            <div className="ex-bundle-price priceInfo" role="region" aria-label="Price Info">
                {!renderPrimary && priceHiddenAnonUser && (
                    <div>
                        <p className="price primary ex-price-anon">-,-</p>
                        <LinkContainer to={{ pathname: pathName, search: location.search }}>
                            <FormattedMessage id="EX_NO_PRICE_AS_ANONYM_MSG" />
                        </LinkContainer>
                    </div>
                )}
                {!renderPrimary && nonFoodCustomer && (
                    <div role="region" aria-label="Price status">
                        <p className="price primary ex-price-status-non-food">-,-</p>
                        <FormattedMessage
                            className="price primary ex-price-status-non-food"
                            id="EX_NO_PRICE_CONTRACT_ONLY_MSG"
                        />
                        <br />
                        <LinkContainer to={{ pathname: pathName, search: location.search }}>
                            <FormattedMessage id="EX_NO_PRICE_LOGIN_MSG" />
                        </LinkContainer>
                    </div>
                )}
                {!renderPrimary && status20User && (
                    <div role="region" aria-label="Price status">
                        <p className="price primary ex-price-status-20">-,-</p>
                        <FormattedMessage
                            className="price primary ex-price-status-20"
                            id="EX_PRICE_NO_LONGER_AVAILABLE_MSG"
                        />
                    </div>
                )}
                {renderPrimary &&
                    primary &&
                    currency &&
                    (primary.renderDescriptionOnly
                        ? this.renderOnlyMessage(primary, primary.style + " price primary")
                        : this.renderPriceWithMessage(
                              primary,
                              currency,
                              dnrBadges,
                              primary.style +
                                  " price primary" +
                                  (isStatus20Employee ? " primary-price-inline price-strikethrough" : "")
                          ))}
                {strikeThrough && currency && (
                    <span className="price strike">
                        <MoneyAmount amount={{ amount: strikeThrough, currency }} />
                    </span>
                )}
                {isStatus20Employee && (
                    <OverlayTrigger
                        trigger={["click", "hover", "focus"]}
                        placement="top"
                        overlay={
                            <Popover id="popoverEmployeeStatus20">
                                <FormattedMessage id={"SD_STATUS_20_EMPLOYEE_MESSAGE"} />
                            </Popover>
                        }>
                        <span className="mfcss_info-icon" />
                    </OverlayTrigger>
                )}
                {renderSecondary &&
                    secondary &&
                    currency &&
                    (secondary.renderDescriptionOnly
                        ? this.renderOnlyMessage(secondary, secondary.style + " price secondary")
                        : this.renderPriceWithMessage(
                              secondary,
                              currency,
                              null,
                              secondary.style + " price secondary" + (isStatus20Employee ? " price-strikethrough" : "")
                          ))}
                {!renderPrimary &&
                    !renderSecondary &&
                    this.props.loggedIn &&
                    // 2022-01 don't show "noprice" for T1/T2 as this is probably no error, we currently never get them.
                    // TODO remove this as soon as we get prices for T1/T2.
                    !this.props.isTier12Customer && (
                        <div className="no-price-available">
                            <NoPrice id={"noprice" + this.props.id} />
                        </div>
                    )}
                {!renderPrimary &&
                    !renderSecondary &&
                    !this.props.priceAndPromotionInfo.priceHiddenReason &&
                    !this.props.loggedIn && (
                        <LinkContainer to={{ pathname: pathName, search: location.search }}>
                            <FormattedMessage id="EX_NO_PRICE_AS_ANONYM_MSG" />
                        </LinkContainer>
                    )}
                {this.renderXForYMessage()}
            </div>
        );
    }
}

const connected = connect(
    {
        stores: [
            [
                SessionStore,
                (store: typeof SessionStore) => ({
                    loggedIn: store.getState().loggedIn,
                    isTier12Customer: isTier12Customer()
                })
            ],
            [PriceConfigStore, ["priceConfig"]]
        ]
    },
    BundlePrice
);

export default connected;
